import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { ToastController } from '@ionic/angular';
import { concat, filter, first, interval } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PendingRequestsInterceptor } from './pending-requests.interceptor';

/** Momento in cui una nuova versione gia' scaricata viene applicata. */
export type UpdateTrigger = 'startup' | 'resume' | 'navigation' | 'idle' | 'manual';

/** Metadati opzionali pubblicati dalla pipeline in ngsw-config.json -> appData. */
interface AppVersionData {
  version?: string;
  /** true = update critico (es. breaking change API): applicato appena e' sicuro, con avviso persistente. */
  critical?: boolean;
}

const PERIODIC_CHECK_MS = 6 * 60 * 60 * 1000;
const CHECK_THROTTLE_MS = 60 * 1000;
const RESUME_MIN_AWAY_MS = 30 * 1000;
const IDLE_MS = 2 * 60 * 1000;
const CRITICAL_IDLE_MS = 15 * 1000;
const STARTUP_WINDOW_MS = 20 * 1000;
const IDLE_POLL_MS = 15 * 1000;
const AUTO_APPLY_COOLDOWN_MS = 5 * 60 * 1000;
const CRITICAL_COOLDOWN_MS = 60 * 1000;
const RECOVERY_COOLDOWN_MS = 60 * 1000;

const KEY_LAST_AUTO_APPLY = 'acu-pwa-last-auto-apply';
const KEY_JUST_UPDATED = 'acu-pwa-just-updated';
const KEY_LAST_RECOVERY = 'acu-pwa-last-recovery';

const OVERLAY_SELECTOR = [
  'ion-modal', 'ion-alert', 'ion-popover', 'ion-action-sheet', 'ion-loading', 'ion-picker'
].map(tag => `${tag}:not(.overlay-hidden)`).join(',');

const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable="true"]';
const AUTH_CALLBACK_PATTERN = /[?&#](code|access_token|id_token|state)=/;

/**
 * Gestisce gli aggiornamenti della PWA senza interrompere l'utente.
 *
 * Il Service Worker scarica la nuova versione in background (VERSION_READY) e fino al
 * reload continua a servire quella corrente in modo consistente. Questo servizio decide
 * QUANDO applicarla, scegliendo un momento in cui il reload e' invisibile:
 *  - startup:    la versione arriva subito dopo l'avvio/ripresa e l'utente non ha ancora interagito
 *  - resume:     l'app torna in primo piano dopo essere stata in background
 *  - navigation: l'utente cambia pagina (diventa un normale caricamento di pagina)
 *  - idle:       nessuna interazione da un paio di minuti
 * Mai con richieste HTTP in corso, overlay aperti, campi in editing o guard di busy attivi.
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private initialized = false;
  private ready = false;
  private critical = false;
  private applying = false;
  private idleTimerStarted = false;

  private readonly bootTime = Date.now();
  private freshMoment = this.bootTime;
  private lastActivity = this.bootTime;
  private hiddenAt: number | null = null;
  private lastCheckTime = 0;

  private readonly busyGuards = new Set<() => boolean>();

  constructor(
    private swUpdate: SwUpdate,
    private router: Router,
    private appRef: ApplicationRef,
    private zone: NgZone,
    private toastCtrl: ToastController,
    private pendingRequests: PendingRequestsInterceptor
  ) {}

  /** Da chiamare una sola volta all'avvio dell'app. */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.showUpdatedToastIfNeeded();

    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.subscribe(evt => this.onVersionEvent(evt));
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('Stato PWA non recuperabile rilevato:', event.reason);
      this.reloadWithCooldown(sessionStorage, KEY_LAST_RECOVERY);
    });

    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(e => this.onNavigationStart(e));
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.checkForUpdate());

    this.zone.runOutsideAngular(() => this.registerDomListeners());

    // Attende la stabilita' dell'app prima del timer periodico (altrimenti isStable non diventa mai true)
    concat(this.appRef.isStable.pipe(first(stable => stable)), interval(PERIODIC_CHECK_MS))
      .subscribe(() => this.checkForUpdate(true));
  }

  /**
   * Permette a una schermata con dati non salvati di bloccare l'aggiornamento automatico.
   * Il guard ritorna true finche' l'utente sta lavorando. Ritorna la funzione di deregistrazione.
   */
  registerBusyGuard(guard: () => boolean): () => void {
    this.busyGuards.add(guard);
    return () => this.busyGuards.delete(guard);
  }

  /** Aggiornamento richiesto esplicitamente dall'utente: ricarica sempre, senza controlli. */
  async applyNow(): Promise<void> {
    await this.apply('manual');
  }

  /**
   * Recupero da errori di caricamento chunk (deploy avvenuto con un client senza SW attivo).
   * Ricarica una sola volta per sessione/minuto. Ritorna false se il ricaricamento e' stato negato.
   */
  recoverFromChunkError(): boolean {
    return this.reloadWithCooldown(sessionStorage, KEY_LAST_RECOVERY);
  }

  static isChunkLoadError(error: any): boolean {
    const message: string = error?.message || error?.rejection?.message || String(error || '');
    return error?.name === 'ChunkLoadError'
      || /Loading chunk [\w-]+ failed/i.test(message)
      || /Failed to fetch dynamically imported module/i.test(message)
      || /error loading dynamically imported module/i.test(message)
      || /Importing a module script failed/i.test(message)
      // Il rewrite di Firebase serve index.html al posto del chunk JS ormai eliminato
      || (/Unexpected token '<'/.test(message) && !!(error?.stack || '').match(/\.js/));
  }

  // ---------------------------------------------------------------------------
  // Eventi del Service Worker
  // ---------------------------------------------------------------------------

  private onVersionEvent(evt: VersionEvent): void {
    if (evt.type === 'VERSION_INSTALLATION_FAILED') {
      console.warn('[PWA Update] Installazione nuova versione fallita:', evt.error);
      return;
    }
    if (evt.type !== 'VERSION_READY') {
      return;
    }

    const data = (evt as VersionReadyEvent).latestVersion.appData as AppVersionData | undefined;
    this.ready = true;
    this.critical = data?.critical === true;
    this.startIdleWatcher();

    if (this.critical) {
      this.presentCriticalToast();
    }

    // Versione arrivata subito dopo l'avvio/ripresa e utente ancora fermo: il reload e' invisibile
    const fresh = this.lastActivity <= this.freshMoment && Date.now() - this.freshMoment < STARTUP_WINDOW_MS;
    if (fresh) {
      void this.tryApply('startup');
    }
  }

  private onNavigationStart(e: NavigationStart): void {
    if (!this.ready || this.applying || e.id === 1 || AUTH_CALLBACK_PATTERN.test(e.url)) {
      return;
    }
    // Su back/forward l'URL e' gia' cambiato: basta ricaricare. Altrimenti si carica direttamente la destinazione.
    const target = e.navigationTrigger === 'popstate' ? undefined : e.url;
    void this.tryApply('navigation', target);
  }

  // ---------------------------------------------------------------------------
  // Listener DOM (fuori da NgZone: non devono scatenare change detection)
  // ---------------------------------------------------------------------------

  private registerDomListeners(): void {
    const markActivity = () => { this.lastActivity = Date.now(); };
    ['pointerdown', 'keydown', 'touchstart', 'wheel'].forEach(name =>
      document.addEventListener(name, markActivity, { capture: true, passive: true })
    );

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.hiddenAt = Date.now();
        return;
      }
      const awayMs = this.hiddenAt ? Date.now() - this.hiddenAt : 0;
      this.hiddenAt = null;
      this.checkForUpdate();
      if (awayMs >= (this.critical ? 0 : RESUME_MIN_AWAY_MS)) {
        this.freshMoment = Date.now();
        void this.tryApply('resume');
      }
    });

    window.addEventListener('focus', () => this.checkForUpdate());
    window.addEventListener('online', () => this.checkForUpdate());
  }

  private startIdleWatcher(): void {
    if (this.idleTimerStarted) {
      return;
    }
    this.idleTimerStarted = true;
    this.zone.runOutsideAngular(() => {
      setInterval(() => {
        const idleMs = this.critical ? CRITICAL_IDLE_MS : IDLE_MS;
        if (this.ready && document.visibilityState === 'visible' && Date.now() - this.lastActivity >= idleMs) {
          void this.tryApply('idle');
        }
      }, IDLE_POLL_MS);
    });
  }

  // ---------------------------------------------------------------------------
  // Controllo e applicazione
  // ---------------------------------------------------------------------------

  private checkForUpdate(force = false): void {
    const now = Date.now();
    if (!force && now - this.lastCheckTime < CHECK_THROTTLE_MS) {
      return;
    }
    this.lastCheckTime = now;
    this.swUpdate.checkForUpdate().catch(err => {
      console.warn('[PWA Update] Errore durante checkForUpdate:', err);
    });
  }

  private isSafeToApply(): boolean {
    if (this.pendingRequests.count > 0) {
      return false;
    }
    if (AUTH_CALLBACK_PATTERN.test(window.location.href)) {
      return false;
    }
    if (document.querySelector(OVERLAY_SELECTOR)) {
      return false;
    }
    const active = document.activeElement;
    if (active && active.matches(EDITABLE_SELECTOR)) {
      return false;
    }
    for (const guard of this.busyGuards) {
      try {
        if (guard()) {
          return false;
        }
      } catch {
        // un guard difettoso non deve bloccare per sempre gli aggiornamenti
      }
    }
    return true;
  }

  private async tryApply(trigger: UpdateTrigger, targetUrl?: string): Promise<void> {
    if (!this.ready || this.applying || !this.isSafeToApply()) {
      return;
    }
    const cooldown = this.critical ? CRITICAL_COOLDOWN_MS : AUTO_APPLY_COOLDOWN_MS;
    if (Date.now() - readStamp(localStorage, KEY_LAST_AUTO_APPLY) < cooldown) {
      return; // protezione anti-loop
    }
    await this.apply(trigger, targetUrl);
  }

  private async apply(trigger: UpdateTrigger, targetUrl?: string): Promise<void> {
    if (this.applying) {
      return;
    }
    this.applying = true;
    try {
      const activated = await this.swUpdate.activateUpdate();
      if (!activated && trigger !== 'manual') {
        this.ready = false;
        this.applying = false;
        return;
      }
    } catch (e) {
      console.error('Errore durante l\'attivazione dell\'aggiornamento:', e);
      if (trigger !== 'manual') {
        this.applying = false;
        return;
      }
    }

    if (trigger !== 'manual') {
      writeStamp(localStorage, KEY_LAST_AUTO_APPLY);
      writeStamp(localStorage, KEY_JUST_UPDATED);
    }

    if (targetUrl) {
      window.location.assign(targetUrl);
    } else {
      window.location.reload();
    }
  }

  private reloadWithCooldown(storage: Storage, key: string): boolean {
    if (Date.now() - readStamp(storage, key) < RECOVERY_COOLDOWN_MS) {
      return false;
    }
    writeStamp(storage, key);
    window.location.reload();
    return true;
  }

  // ---------------------------------------------------------------------------
  // UI minimale e non bloccante
  // ---------------------------------------------------------------------------

  private async presentCriticalToast(): Promise<void> {
    try {
      const toast = await this.toastCtrl.create({
        message: 'È disponibile un aggiornamento importante di Acuplace.',
        position: 'bottom',
        buttons: [{ text: 'Aggiorna ora', handler: () => { void this.applyNow(); } }]
      });
      await toast.present();
    } catch (e) {
      console.warn('[PWA Update] Impossibile mostrare il toast di aggiornamento:', e);
    }
  }

  private showUpdatedToastIfNeeded(): void {
    if (!readStamp(localStorage, KEY_JUST_UPDATED)) {
      return;
    }
    removeStamp(localStorage, KEY_JUST_UPDATED);
    this.appRef.isStable.pipe(first(stable => stable)).subscribe(async () => {
      try {
        const toast = await this.toastCtrl.create({
          message: `Acuplace aggiornata alla versione ${environment.acuplaceVersion}`,
          duration: 3500,
          position: 'bottom'
        });
        await toast.present();
      } catch {
        // solo cortesia: nessun impatto se fallisce
      }
    });
  }
}

function readStamp(storage: Storage, key: string): number {
  try {
    return Number(storage.getItem(key)) || 0;
  } catch {
    return 0;
  }
}

function writeStamp(storage: Storage, key: string): void {
  try {
    storage.setItem(key, String(Date.now()));
  } catch {
    // storage non disponibile (private mode): si perde solo il cooldown
  }
}

function removeStamp(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // ignorato
  }
}
